import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, isDevMode } from '@angular/core';
import { FirebaseRemoteConfigService } from '@core/services/common/firebase-remote-config.service';
import { Platform } from '@ionic/angular';

type SeverityType = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug' | 'critical';
@Injectable({
  providedIn: 'root'
})
export class SentryErrorHandler extends ErrorHandler {

  private static Sentry: any;

  constructor(private platform: Platform, private firebaseConfig: FirebaseRemoteConfigService) {
    super();
  }

  static sendServerError(error: HttpErrorResponse) {
    const { code, message } = this.getCodeAndMessage(error);
    SentryErrorHandler.Sentry?.captureEvent({
      message: `ServerError [${code}] ${message}`,
      level: 'warning',
      transaction: `[${error.status}] ${error.url}`,
      extra: { response: error, responseBody: error?.error?.data || error?.error }
    });
  }

  static sendServerErrorHandled(error: HttpErrorResponse, errorCode: string, level: SeverityType) {
    const code = errorCode || error?.status;
    const message = this.getCodeAndMessage(error).message;
    SentryErrorHandler.Sentry?.captureEvent({
      level,
      message: `ServerErrorHandled [${code}] ${message}`,
      transaction: `[${error.status}] ${error.url}`,
      extra: { response: error, responseBody: error?.error?.data || error?.error }
    });
  }

  static sendCustomError(message: string, level: SeverityType, transaction: string, error: any) {
    SentryErrorHandler.Sentry?.captureEvent({
      level,
      message,
      transaction,
      extra: { response: error }
    });
  }

  static sendCustomMessage(message: string, severity: SeverityType) {
    SentryErrorHandler.Sentry?.captureMessage(message, severity);
  }

  async init(dsn: string, release: string, environment: string) {
    const TracingIntegrations = await import('@sentry/tracing');
    await this.initialize();
    const ignoreErrors = await this.getIgnoreErrorsList();

    SentryErrorHandler.Sentry.init({
      dsn,
      release,
      environment,
      ignoreErrors,
      transport: SentryErrorHandler.Sentry.Transports.FetchTransport,
      beforeSend: event => {
        if (event?.tags?.url && typeof event.tags.url !== 'symbol') {
          const url = '' + event.tags.url;
          event.tags.url = new URL(url).pathname;
        }
        const stack = event.stacktrace || event.exception?.values[0].stacktrace;
        if (stack) {
          stack.frames.forEach(frame => {
            frame.filename = frame.filename.substring(frame.filename.lastIndexOf('/'));
          });
        }
        return event;
      },
      integrations: [new TracingIntegrations.BrowserTracing()],
      tracesSampleRate: 0.1
    });
  }

  async setSentryUser(user: { id: string }) {
    await this.initialize();
    SentryErrorHandler.Sentry.setUser(user);
  }

  async sentryConfigureScope(callback: (scope: any) => void) {
    await this.initialize();
    SentryErrorHandler.Sentry.configureScope(callback);
  }

  handleError(error: any) {
    if (error?.handledByInterceptor) {
      return;
    }
    if (isDevMode()) {
      console.log('ErrorHandler:', error.originalError || error);
      return;
    }

    if (error instanceof HttpErrorResponse) {
      SentryErrorHandler.sendServerError(error);
    } else {
      SentryErrorHandler.Sentry?.captureException(error.originalError || error);
    }
  }

  private async initialize() {
    if (!SentryErrorHandler.Sentry) {
      SentryErrorHandler.Sentry = await import('@sentry/browser');
    }
  }

  private static getCodeAndMessage(error: any) {
    const code = error?.status;
    const message = error?.url ? new URL(error.url, 'https://localhost').pathname : 'Unknown';
    return { code, message };
  }

  private async getIgnoreErrorsList() {
    const value = await this.firebaseConfig.getConfigValue('errorSentryWhitelist').catch(() => undefined);
    const appKnownErrors: string[] = !!value?.asString() ? JSON.parse(value.asString()) : [];
    return appKnownErrors;
  }
}
